import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { SessionHeader } from '../../../../../packages/ui/templates/sessions/session-header';
import { SessionNavigator } from '../../../../../packages/ui/templates/sessions/session-navigator';
import { SectionDetail } from '../../../../../packages/ui/templates/sessions/section-detail';
import EndSession from '../../../../../packages/ui/organisms/end-session-modal';
import { KeyboardShortcutsModal } from '../../../../../packages/ui/organisms/keyboard-shortcuts/KeyboardShortcutsModal';
import { Sections, subsections } from '../../../../../packages/ui/templates/sessions/sessions.constants';
import AddEvaluation from '../../../../admin/appointments/appointment-detail-drawer/AddEvaluation';
import RejectEvaluationModal from '../../evaluations-v2/evaluation-modals';
import ConfirmUsePreviousModal from '../../../../../packages/ui/organisms/confirm-use-previous-modal';
import SessionModalPopup from '../SessionModalPopup';
import history from '../../../../../utils/history';
import dayjs from 'dayjs';
import { getUserTimeZone } from '../../../../../utils/dayjs';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import {
  updateAppointmentEvaluationStatus,
  updateEvaluationContext,
  getExpandedEvaluation,
  getEvaluationContext,
} from '../../../../../services/conversation/conversation.service';
import { conversationActionCreators } from '../../../../../redux/modules/conversation';
import {
  selectAssignedEvaluationState,
  selectEvaluation,
} from '../../../../../redux/modules/conversation/selectors';
import useEvaluationHead from '../../../../../hooks/useEvaluationHead';
import { EvaluationHelper } from '../../../../../utils/EvaluationHelper';
import { ExerciseHelper } from '../../../../../utils/ExerciseHelper';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import { getAppointmentById } from '../../../../../services/appointment/appointment.service';
import { CbStatus } from '../../../../../redux/modules/conversation/types';
import {
  getPatientModalities,
  getPatientTreatmentPlan,
} from '../../../../../services/profile/profile.service';
import { sx } from './SessionContent.styles';
import { Icons } from '../../../../../packages/icons';
import { btnSize, btnType, Button } from '../../../../../packages/ui/atoms/button';
import { fontWeight, Text, textLevel } from '../../../../../packages/ui/atoms/typography';
import { colors } from '../../../../../packages/colors';
import analysisInactiveIcon from '../../../../../assets/images/cross-red.png';

const list = new EvaluationHelper();
const exerciseList = new ExerciseHelper();

/**
 * SessionContent Component
 * Handles all session navigation, evaluations, and data collection logic
 */
const SessionContent = ({
  appointment,
  endSession,
  setToastProperties,
  nextAppointment,
  nextGeneralAppointment,
  handleMemberActionClick,
  onRestartSessionClick,
  sessionEnded,
  setSessionEnded,
  demographicDetails,
  onScratchpadUpdate,
  showMarkAsNoShow = false,
}) => {
  const dispatch = useDispatch();
  const isPrescriber =
    appointment?.designation === 'Nurse Practitioner' || appointment?.designation === 'Physician';
  const { meta } = useSelector(getAuth);
  const [selectedSection, setSelectedSection] = useState(Sections.WELCOME);
  const [openAddEvaluation, setOpenAddEvaluation] = useState(false);
  const { evaluations } = useSelector(selectAssignedEvaluationState);
  const activeEvaluations =
    evaluations?.filter(evaluation => evaluation.status !== 'REJECTED' && !evaluation.isRecommended) || [];
  const [currentBlock, setCurrentBlock] = useState(null);
  const [currentCbId, setCurrentCbId] = useState(null);
  const [nextInProgress, setNextInProgress] = useState(false);
  const [selectedSubsection, setSelectedSubsection] = useState(subsections.IMPORTANT_INFORMATION);
  const [duration, setDuration] = useState(0);
  const [modalities, setModalities] = useState([]);
  const [sessionStarted] = useState(true);
  const [endSessionModalVisible, setEndSessionModalVisible] = useState(false);
  const [shortcutsVisible, setShortcutsVisible] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [textInputInFocus, setTextInputInFocus] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [leftTrigger, setLeftTrigger] = useState(false);
  const [rightTrigger, setRightTrigger] = useState(false);
  const [currentEvaluation, setCurrentEvaluation] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const isSessionEndedRef = useRef(false);
  const [dctPreviousResponses, setDctPreviousResponses] = useState([]);
  const [previousResponses, setPreviousResponses] = useState([]);
  const [dctSelectedResponses, setDctSelectedResponses] = useState([]);
  const [evaluationsCompleted, setEvaluationsCompleted] = useState(false);
  const [isRequiredQuestionModalOpen, setIsRequiredQuestionModalOpen] = useState(false);
  const [isHaveAllQuestionsBeenAskedModalOpen, setIsHaveAllQuestionsBeenAskedModalOpen] = useState(false);
  const [jumpToEvaluation, setJumpToEvaluation] = useState(null);
  const [usedEvaluationId, setUsedEvaluationId] = useState(null);
  const [evaluationToReject, setEvaluationToReject] = useState(null);
  const [previousResponseFlags, setPreviousResponseFlags] = useState(null);
  const [evaluationClicked, setEvaluationClicked] = useState(true);
  const [profileElements, setProfileElements] = useState({});
  const [expandedConversation, setExpandedConversation] = useState(null);
  const allEvaluations = useSelector(selectEvaluation);
  const { head, processEvaluationData } = useEvaluationHead();
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [scrollToTop, setScrollToTop] = useState(true);
  const [aiMode, setAiMode] = useState(true);
  const [scratchpadData, setScratchpadData] = useState({ scratchpad: '', addScratchpadNotes: null });
  const [isCompactMode, setIsCompactMode] = useState(false);
  const contentRef = useRef(null);

  const handleScratchpadUpdate = data => {
    setScratchpadData(data);
    if (onScratchpadUpdate) {
      onScratchpadUpdate({ ...data, currentSection: selectedSection });
    }
  };

  // Update parent with current section whenever it changes
  useEffect(() => {
    if (onScratchpadUpdate) {
      onScratchpadUpdate({ ...scratchpadData, currentSection: selectedSection });
    }
  }, [selectedSection]);

  // Initial update to send scratchpad data on mount
  useEffect(() => {
    if (onScratchpadUpdate && scratchpadData.scratchpad) {
      onScratchpadUpdate({ ...scratchpadData, currentSection: selectedSection });
    }
  }, [scratchpadData.scratchpad]);
  const isMountedRef = useRef(true);
  const isMoveNextAllowed = useRef(true);

  const startSignoff = () => {
    history.replace(`/provider/appointments/current/${appointment.appointmentId}/session-signoff`);
  };

  const goToSchedule = () => {
    history.replace(
      `/provider/${meta.userId}/day-schedule?date=${dayjs().tz(getUserTimeZone()).format('YYYY-MM-DD')}`,
    );
  };

  const onAddEvaluation = selectedValues => {
    dispatch(
      conversationActionCreators.addAssignedEvaluation({
        evaluationIds: selectedValues,
        appointmentId: appointment?.appointmentId,
      }),
    );
    setOpenAddEvaluation(false);
    if (evaluationsCompleted) {
      setUsedEvaluationId(selectedValues[0]);
    }
  };

  const handleEvaluationDeletion = evaluationId => {
    const isAssigned = evaluations.some(evalId => evalId.id === evaluationId);
    if (isAssigned) {
      const { appointmentId } = appointment;
      dispatch(conversationActionCreators.deleteEvaluation({ appointmentId, evaluationId }));
    }
  };

  const showEndSessionWarning = () => {
    setEndSessionModalVisible(true);
  };

  const onEndSession = () => {
    setEndSessionModalVisible(false);
    setSessionEnded(true);
    setSelectedSection(Sections.CLOSE_SESSION);
    setSelectedSubsection(null);
    endSession();
  };

  const openEvaluationsDrawer = () => {
    setOpenAddEvaluation(true);
  };

  const fetchAppointmentDetails = () => {
    getAppointmentById({ appointmentId: appointment.appointmentId })
      .then(res => {
        setAppointmentDetails(res.data);
      })
      .catch(e => console.log(e));
  };

  const fetchTreatmentPlan = async () => {
    try {
      const { data } = await getPatientTreatmentPlan(appointment.patientId, { active: true });
      setTreatmentPlan(data);
    } catch (error) {
      console.error('Error fetching treatment plan:', error);
    }
  };

  const fetchPatientModalities = async () => {
    try {
      const response = await getPatientModalities(appointment.providerId);
      if (response?.data) {
        setModalities(response.data.modalities);
      }
    } catch (error) {
      console.error('Error fetching patient modalities:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load patient modalities. Please try again.',
        }),
      );
    }
  };

  const mergeUserResponses = (evaluation, expandedEvaluation) => {
    const responseMap = new Map();
    evaluation.cbResponseList.forEach(cb => {
      responseMap.set(cb.cbId, {
        userResponse: cb.userResponse,
        usePreviousResponses: cb.usePreviousResponses,
      });
      if (Array.isArray(cb.dctContentBlockList)) {
        cb.dctContentBlockList.forEach(nestedCb => {
          responseMap.set(nestedCb.cbId, {
            userResponse: nestedCb.userResponse,
            usePreviousResponses: nestedCb.usePreviousResponses,
          });
        });
      }
    });

    const applyMerge = blocks => {
      blocks.forEach(cb => {
        const match = responseMap.get(cb.cbId);
        if (match) {
          cb.userResponse = match.userResponse ?? cb.userResponse;
          cb.usePreviousResponses = match.usePreviousResponses ?? cb.usePreviousResponses;
        }
        if (Array.isArray(cb.dctContentBlockList)) {
          applyMerge(cb.dctContentBlockList);
        }
      });
    };
    applyMerge(expandedEvaluation.evaluation.contentBlocks);
    return expandedEvaluation;
  };

  useEffect(() => {
    void fetchAppointmentDetails();
  }, []);

  useEffect(() => {
    if (usedEvaluationId && evaluations) {
      const foundEvaluation = evaluations.find(evaluation => evaluation.id === usedEvaluationId);
      if (foundEvaluation && foundEvaluation.status !== 'REJECTED') {
        setUsedEvaluationId(null);
        setEvaluationsCompleted(null);
        setCurrentEvaluation(foundEvaluation);
      }
    }
  }, [evaluations, usedEvaluationId]);

  useEffect(() => {
    if (appointment) {
      dispatch(
        conversationActionCreators.fetchAssignedEvaluations({ appointmentId: appointment.appointmentId }),
      );
      void fetchTreatmentPlan();
      void fetchPatientModalities();
      dispatch(conversationActionCreators.fetchEvaluations());
    }
  }, [appointment]);

  useEffect(() => {
    if (currentEvaluation && evaluationClicked) {
      list.setHead(null);
      exerciseList.setHead(null);
      setEvaluationsCompleted(false);
      setEvaluationClicked(false);

      // Check if this is an EXERCISE type
      if (currentEvaluation.type === 'EXERCISE') {
        // For exercises, we only need getExpandedEvaluation (no context needed)
        getExpandedEvaluation(appointment?.appointmentId, currentEvaluation?.id, true)
          .then(response => {
            const { data: expandedExercise } = response;
            const { evaluation: exercise } = expandedExercise;
            setExpandedConversation(exercise);
            exerciseList.transformExercise(exercise);
          })
          .catch(error => {
            console.error('Error fetching exercise:', error);
          });
      } else {
        // For evaluations, we need both getExpandedEvaluation and getContext
        const getEvaluation = getExpandedEvaluation(appointment?.appointmentId, currentEvaluation?.id, true);
        const getContext = getEvaluationContext({
          appointmentId: appointment?.appointmentId,
          evaluationId: currentEvaluation?.id,
        });
        Promise.all([getEvaluation, getContext]).then(([evaluation, context]) => {
          const { data: evaluationContext } = context;
          const { data: expandedEvaluation } = evaluation;
          const newExpandedEvalualtions = mergeUserResponses(evaluationContext, expandedEvaluation);
          const { evaluation: conversation, profileElements } = newExpandedEvalualtions;
          setProfileElements(profileElements, newExpandedEvalualtions);
          setExpandedConversation(conversation);
        });
      }
    }
    return () => {
      // Cleanup context
    };
  }, [currentEvaluation, evaluationClicked]);

  useEffect(() => {
    if (expandedConversation) {
      // Only process evaluation data for EVALUATION type, not EXERCISE type
      if (currentEvaluation?.type !== 'EXERCISE') {
        processEvaluationData(expandedConversation, profileElements);
      }
    }
  }, [expandedConversation]);

  useEffect(() => {
    if (head) {
      if (!list.getHead()) {
        list.setHead(head);
      } else {
        list.setHead(head);
        setNextBlock(currentBlock, head);
      }
    }
  }, [head]);

  const updateEvaluationStatus = async request => {
    let payload;
    if (request) {
      payload = request;
    } else {
      payload = {
        evaluationUpdatedStatus: 'COMPLETED',
        evaluationId: currentEvaluation.id,
        remarks: 'Evaluation completed',
      };
    }
    const { data } = await updateAppointmentEvaluationStatus(payload, {
      appointmentId: appointment.appointmentId,
    });
    if (data.errors) {
      console.warn(data.errors);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: data.errors?.[0]?.endUserMessage || 'Something went wrong',
        }),
      );
    } else {
      if (request) {
        dispatch(
          conversationActionCreators.fetchAssignedEvaluations({
            appointmentId: appointment.appointmentId,
          }),
        );
      }
    }
  };

  const completeEvaluation = async () => {
    // For exercises, just mark as completed (no submission needed)
    if (currentEvaluation?.type === 'EXERCISE') {
      void updateEvaluationStatus();
      if (currentEvaluation && activeEvaluations.length > 0) {
        const currentIndex = activeEvaluations.findIndex(e => e.id === currentEvaluation.id);
        if (currentIndex < activeEvaluations.length - 1) {
          exerciseList.setHead(null);
          setCurrentBlock(null);
          setCurrentEvaluation(activeEvaluations[currentIndex + 1]);
          setEvaluationClicked(true);
        } else {
          setEvaluationsCompleted(true);
          exerciseList.setHead(null);
          setCurrentBlock(null);
          setCurrentEvaluation(null);
        }
      }
      return;
    }

    // For evaluations, submit and complete as usual
    const evalCompleted = list.isEvaluationCompleted();
    let allCbSubmitted = true;
    if (evalCompleted) {
      allCbSubmitted = await submitAllCb();
      void updateEvaluationStatus();
    }
    if (currentEvaluation && activeEvaluations.length > 0 && allCbSubmitted) {
      const currentIndex = activeEvaluations.findIndex(e => e.id === currentEvaluation.id);
      if (currentIndex < activeEvaluations.length - 1) {
        list.setHead(null);
        setCurrentBlock(null);
        setCurrentEvaluation(activeEvaluations[currentIndex + 1]);
        setEvaluationClicked(true);
      } else {
        setEvaluationsCompleted(true);
        list.setHead(null);
        setCurrentBlock(null);
        setCurrentEvaluation(null);
      }
    }
  };

  const moveNext = cbId => {
    // For exercises, use exerciseList helper
    if (currentEvaluation?.type === 'EXERCISE') {
      const next = exerciseList.getNextBlock(cbId);
      if (next) {
        setCurrentBlock(next);
      } else {
        completeEvaluation();
      }
      setNextInProgress(false);
      return;
    }

    // For evaluations, use list helper
    let next = list.getNextApplicableBlock(cbId, true);
    while (next && dctSelectedResponses.some(response => response.cbId === next.cbId)) {
      next = list.getNextApplicableBlock(next.cbId, true);
    }
    if (next) {
      setCurrentBlock(next);
    } else {
      completeEvaluation();
    }
    setNextInProgress(false);
  };

  const setNextBlock = (block, context, forceNext = false, isRequiredQuestionSkipped = false) => {
    if (!isMoveNextAllowed.current) {
      const currentBlock = list.getContentBlock(block.cbId);
      setCurrentBlock(currentBlock);
      return;
    }
    if (block) {
      if (nextInProgress || forceNext) {
        let isAIEnabled = false;
        let value = null;
        const currentBlock = list.getContentBlock(block.cbId);
        if (block.fullPage) {
          if (!isRequiredQuestionSkipped) {
            setCurrentBlock(list.getContentBlock(block.cbId));
            return;
          }
        } else {
          if (!appointment?.sessionAnalysisAllowed || block?.forceManualEntry) {
            isAIEnabled = false;
          } else {
            isAIEnabled = aiMode;
          }
          if (block?.userResponse?.userResponseList?.length > 0) {
            value = block.userResponse.userResponseList[0];
          } else if (Array.isArray(block?.userResponse) && block?.userResponse?.length > 0) {
            value = block?.userResponse[0];
          }
          if (block.required && !value && !isRequiredQuestionSkipped && !isAIEnabled) {
            setCurrentBlock(list.getContentBlock(block.cbId));
            return;
          }
        }
        const cbId = block.cbId;
        const index = expandedConversation.contentBlocks.findIndex(item => item.cbId === block.cbId);
        if (index >= 0 && index < expandedConversation.contentBlocks.length) {
          const nextCb = expandedConversation.contentBlocks[index + 1];
          if (!!nextCb?.displayLogics || !!nextCb?.basicInfo?.questionFrequency) {
            let isAttemptable = list.evaluateDisplayLogic(nextCb, expandedConversation, profileElements);
            if (isAttemptable) {
              const filteredCBs = expandedConversation.contentBlocks.filter(item => item.cbId === cbId);
              if (filteredCBs[0]?.basicInfo?.questionFrequency) {
                isAttemptable = list.evaluateQuestionFrequency(filteredCBs[0]);
              }
            }
            expandedConversation.contentBlocks = expandedConversation.contentBlocks.map(cb =>
              cb.cbId === nextCb.cbId ? { ...cb, isAttemptable } : cb,
            );
            list.transformEvaluation(expandedConversation, profileElements);
            if (isAttemptable && !!nextCb?.displayLogics && !nextCb?.basicInfo?.questionFrequency) {
              if (dctSelectedResponses.some(response => response.cbId === nextCb.cbId)) {
                moveNext(nextCb.cbId);
              } else {
                setCurrentBlock(list.getContentBlock(nextCb.cbId));
              }
            } else {
              moveNext(cbId);
            }
          } else {
            moveNext(cbId);
          }
        }
      } else {
        setCurrentBlock(list.getContentBlock(block.cbId));
      }
    } else {
      if (context.status !== CbStatus.ANSWERED) {
        setCurrentBlock(context);
      } else {
        setNextBlock(context, context, true);
      }
    }
  };

  const setNewUserResponse = (contentBlocks, cbIdToUpdate, newUserResponse) => {
    const update = blocks => {
      blocks.forEach(cb => {
        if (cb.cbId === cbIdToUpdate) {
          cb.userResponse = newUserResponse;
        }
        if (Array.isArray(cb.dctContentBlockList)) {
          update(cb.dctContentBlockList);
        }
      });
    };
    update(contentBlocks);
  };

  const handleNext = (currentBlock, isRequiredQuestionSkipped) => {
    let isAIEnabled = false;
    let value = null;
    if (!appointment?.sessionAnalysisAllowed || currentBlock?.forceManualEntry) {
      isAIEnabled = false;
    } else {
      isAIEnabled = aiMode;
    }
    if (currentBlock?.userResponse?.userResponseList?.length > 0) {
      value = currentBlock.userResponse.userResponseList[0];
    } else if (Array.isArray(currentBlock?.userResponse) && currentBlock?.userResponse?.length > 0) {
      value = currentBlock?.userResponse[0];
    }
    if (currentBlock.required && !value && !isRequiredQuestionSkipped && !isAIEnabled) {
      setCurrentBlock(list.getContentBlock(currentBlock.cbId));
      setIsRequiredQuestionModalOpen(true);
      return;
    } else {
      isMoveNextAllowed.current = true;
      setNextBlock(currentBlock, head, true, isRequiredQuestionSkipped);
    }
  };

  const submitAnswer = async (isRequiredQuestionSkipped = false) => {
    if (currentBlock) {
      let payload = null;
      if (currentBlock.cbType === 'dct') {
        if (currentBlock.fullPage) {
          const { children } = currentBlock;
          if (children.length > 0) {
            expandedConversation.contentBlocks.forEach(contentBlock => {
              children.forEach(child => {
                if (child.cbId === contentBlock.cbId) {
                  contentBlock.userResponse = { userResponseList: child.evaluationResponses };
                }
              });
            });
            if (!isRequiredQuestionSkipped) {
              setIsHaveAllQuestionsBeenAskedModalOpen(true);
              return;
            }
            payload = children
              .map(child => {
                if (child.userResponse) {
                  const evaluationResponses = child.userResponse.userResponseList;
                  if (
                    !evaluationResponses ||
                    evaluationResponses.length === 0 ||
                    (evaluationResponses && evaluationResponses.length === 1 && evaluationResponses[0] === '')
                  ) {
                    return null;
                  }
                  return {
                    contentBlockId: child.cbId,
                    dctId: currentBlock.dctId,
                    evaluationResponses,
                    providerNarrative: '',
                  };
                }
                return null;
              })
              .filter(response => response !== null);
            if (Array.isArray(payload) && payload.length === 0) {
              payload = null;
            }
          }
        } else {
          if (dctSelectedResponses.length > 0) {
            payload = dctSelectedResponses.map(response => {
              return {
                contentBlockId: response.cbId,
                dctId: currentBlock.dctId,
                evaluationResponses: response.answers.map(ans => ans.value),
                providerNarrative: '',
              };
            });
            currentBlock.children.forEach(child => {
              if (dctSelectedResponses.some(response => response.cbId === child.cbId)) {
                child.oldIsAttemptable = child.isAttemptable;
                child.isAttemptable = false;
              }
            });
          }
        }
      } else {
        if (currentBlock.userResponse) {
          const { userResponse } = currentBlock;
          let evaluationResponses = userResponse?.userResponseList ?? userResponse;

          if (evaluationResponses && evaluationResponses.length === 1 && evaluationResponses[0] === '')
            evaluationResponses = [];
          payload = [
            {
              contentBlockId: currentBlock.cbId,
              dctId: currentBlock.dctId,
              evaluationResponses,
              providerNarrative: userResponse?.providerNarrative || '',
            },
          ];
        }
      }
      if (payload) {
        setNewUserResponse(
          expandedConversation.contentBlocks,
          currentBlock.cbId,
          payload[0].evaluationResponses,
        );
        updateEvaluationContext(payload, {
          appointmentId: appointment.appointmentId,
          evaluationId: currentEvaluation.id,
        }).then(({ data }) => {
          if (data.errors) {
            console.warn(data.errors);
          }
        });
        handleNext(currentBlock, isRequiredQuestionSkipped);
      } else {
        handleNext(currentBlock, isRequiredQuestionSkipped);
      }
    }
  };

  const submitAllCb = async () => {
    try {
      let payload = expandedConversation.contentBlocks.map(item => ({
        contentBlockId: item?.cbId,
        evaluationResponses: item?.userResponse?.userResponseList ?? item?.userResponse ?? null,
        providerNarrative: '',
      }));
      if (expandedConversation?.dcts) {
        payload.forEach(item => {
          expandedConversation.dcts.forEach(dct => {
            if (dct.contentBlocks.includes(item.contentBlockId)) {
              item.dctId = dct.dctId;
            }
          });
        });
      }
      payload = payload.filter(answer => answer.evaluationResponses != null);
      const { data } = await updateEvaluationContext(payload, {
        appointmentId: appointment.appointmentId,
        evaluationId: currentEvaluation.id,
      });
      if (data.errors) {
        console.warn(data.errors);
        return false;
      }
      return true;
    } catch (error) {
      console.error('API call failed:', error);
      return false;
    }
  };

  const respondAndGoNext = () => {
    setNextInProgress(true);
    void submitAnswer();
  };

  const onPrevious = () => {
    if (selectedSection === Sections.WELCOME) {
      if (appointmentDetails?.firstAppointment && selectedSubsection === subsections.TREATMENT_PLAN) {
        setSelectedSubsection(subsections.DOUBLE_CHECK_IDS);
      } else {
        setSelectedSubsection(subsections.IMPORTANT_INFORMATION);
      }
    } else if (selectedSection === Sections.EVALUATIONS) {
      setScrollToTop(true);
      if (currentBlock) {
        // Handle exercises differently
        if (currentEvaluation?.type === 'EXERCISE') {
          const prev = exerciseList.getPreviousBlock(currentBlock.cbId);
          if (prev) {
            setCurrentBlock(prev);
          } else {
            setCurrentBlock(null);
          }
        } else {
          // Handle evaluations as before
          let prev = list.getPreviousApplicableBlock(currentBlock.cbId, false);
          if (prev) {
            prev.children.forEach(child => {
              if (child?.oldIsAttemptable) {
                child.isAttemptable = child.oldIsAttemptable;
              }
            });
            prev = list.getPreviousApplicableBlock(currentBlock.cbId, false);
            setCurrentBlock(prev);
          } else {
            setCurrentBlock(null);
          }
        }
      } else {
        if (currentEvaluation) {
          const currentIndex = activeEvaluations.findIndex(e => e.id === currentEvaluation.id);
          if (currentIndex > 0) {
            setCurrentEvaluation(activeEvaluations[currentIndex - 1]);
          } else {
            setCurrentEvaluation(null);
            setSelectedSubsection(null);
          }
        } else {
          setSelectedSection(Sections.WELCOME);
          setEvaluationsCompleted(false);
          const firstAppointment = appointmentDetails?.firstAppointment;
          if (treatmentPlan) {
            setSelectedSubsection(subsections.TREATMENT_PLAN);
          } else if (firstAppointment) {
            setSelectedSubsection(subsections.DOUBLE_CHECK_IDS);
          } else {
            setSelectedSubsection(subsections.IMPORTANT_INFORMATION);
          }
          setCurrentBlock(null);
          setCurrentEvaluation(null);
        }
      }
    } else if (selectedSection === Sections.OPEN_SESSION) {
      if (isPrescriber) {
        setSelectedSection(Sections.MEDICATIONS);
      } else {
        setSelectedSection(Sections.EVALUATIONS);
      }
    } else if (selectedSection === Sections.MEDICATIONS) {
      setSelectedSection(Sections.EVALUATIONS);
    } else if (selectedSection === Sections.CLOSE_SESSION) {
      setSelectedSection(Sections.OPEN_SESSION);
    }
  };

  const getPreviousResponsesForDCT = cbBlock => {
    const filteredDCT = expandedConversation.dcts.find(dct => dct.dctId === cbBlock.dctId);

    let previousResponse = (filteredDCT?.contentBlocks || [])
      .map(cbId => {
        const cb = expandedConversation.contentBlocks.find(cb => cb.cbId === cbId);
        if (!cb) return null;
        const prevResp = getUserResponseInfo(cb.previousResponses);
        return {
          cbId,
          answers: [...prevResp],
        };
      })
      .filter(Boolean);
    const filteredPreviousResponse = previousResponse.filter(item => item.answers.length > 0);
    setDctPreviousResponses(filteredPreviousResponse);
  };

  const onNext = () => {
    if (selectedSection === Sections.WELCOME) {
      if (selectedSubsection === subsections.IMPORTANT_INFORMATION) {
        const firstAppointment = appointmentDetails?.firstAppointment;
        if (firstAppointment) {
          setSelectedSubsection(subsections.DOUBLE_CHECK_IDS);
        } else if (
          (selectedSubsection === subsections.IMPORTANT_INFORMATION ||
            selectedSubsection === subsections.DOUBLE_CHECK_IDS) &&
          treatmentPlan
        ) {
          setSelectedSection(Sections.WELCOME);
          setSelectedSubsection(subsections.TREATMENT_PLAN);
        } else {
          setSelectedSection(Sections.EVALUATIONS);
          setSelectedSubsection(null);
        }
      } else {
        setSelectedSection(Sections.EVALUATIONS);
        setSelectedSubsection(null);
      }
    } else if (selectedSection === Sections.EVALUATIONS) {
      setScrollToTop(true);
      if (!currentEvaluation) setEvaluationClicked(true);
      if (evaluationsCompleted) {
        if (isPrescriber) {
          setSelectedSection(Sections.MEDICATIONS);
        } else {
          setSelectedSection(Sections.OPEN_SESSION);
        }
        setEvaluationsCompleted(false);
        setCurrentEvaluation(null);
        setCurrentBlock(null);
        list.setHead(null);
      } else {
        if (!currentEvaluation) {
          if (activeEvaluations.length > 0) {
            setCurrentEvaluation(activeEvaluations[0]);
          } else {
            if (isPrescriber) {
              setSelectedSection(Sections.MEDICATIONS);
            } else {
              setSelectedSection(Sections.OPEN_SESSION);
            }
          }
        } else {
          // Handle exercises differently than evaluations
          if (currentEvaluation?.type === 'EXERCISE') {
            if (exerciseList.getHead()) {
              if (currentBlock) {
                // For exercises, just move to next step
                const next = exerciseList.getNextBlock(currentBlock.cbId);
                if (next) {
                  setCurrentBlock(next);
                } else {
                  completeEvaluation();
                }
              } else {
                // Start from first exercise step
                setCurrentBlock(exerciseList.getHead());
              }
            }
          } else {
            // Handle evaluations as before
            if (list.getHead()) {
              if (currentBlock) {
                if (currentBlock.cbType === 'dct') {
                  getPreviousResponsesForDCT(currentBlock);
                }
                respondAndGoNext();
              } else {
                let cbBlock = list.getHead();
                let next = list.getNextApplicableBlock(cbBlock.cbId, true);
                if (cbBlock?.cbType === 'dct') {
                  getPreviousResponsesForDCT(cbBlock);
                  setNextBlock(currentBlock, head, true);
                } else {
                  if (!cbBlock.isAttemptable) {
                    setCurrentBlock(next);
                  } else {
                    setNextBlock(currentBlock, head, true);
                  }
                }
              }
            }
          }
        }
      }
    } else if (selectedSection === Sections.MEDICATIONS) {
      setSelectedSection(Sections.OPEN_SESSION);
    } else if (selectedSection === Sections.OPEN_SESSION && sessionEnded) {
      setSelectedSection(Sections.CLOSE_SESSION);
    }
  };

  const nextQuestionByKey = e => {
    e.preventDefault();
    e.target.blur();
    onNext();
  };

  const getUserResponseInfo = userResp => {
    if (Array.isArray(userResp)) {
      return userResp.map(item => ({
        score: 0,
        value: item ?? null,
      }));
    }
    return [];
  };

  const getPreviousResponses = async () => {
    try {
      if (currentBlock?.cbType === 'dct') {
        getPreviousResponsesForDCT(currentBlock);
      } else {
        const cb = expandedConversation.contentBlocks.find(cb => cb.cbId === currentBlock.cbId);
        const prevResp = getUserResponseInfo(cb?.previousResponses);
        setPreviousResponses(
          prevResp.length !== 0
            ? [
                {
                  cbId: currentBlock.cbId,
                  answers: prevResp,
                },
              ]
            : [],
        );
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const handleKeyboardShortcuts = useCallback(
    e => {
      if (shortcutsVisible === true || textInputInFocus) {
        return;
      }

      switch (e.key) {
        case ' ':
          if (e.shiftKey) {
            e.preventDefault();
            e.target.blur();
            onPrevious();
          } else {
            nextQuestionByKey(e);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (selectedIndex >= 0) {
            setSelectedIndex(selectedIndex - 1);
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (currentBlock.cbType === 'rating-scale' && currentBlock.ratingScale) {
            if (selectedIndex < currentBlock.ratingScale.values.length - 1) {
              setSelectedIndex(selectedIndex + 1);
            }
          } else {
            if (selectedIndex < currentBlock.choices.length - 1) {
              setSelectedIndex(selectedIndex + 1);
            }
          }
          break;
        case 'ArrowRight':
          if (selectedIndex > -1 || (currentBlock && currentBlock.cbType === 'rating-scale')) {
            setRightTrigger(prev => !prev);
          }
          break;

        case 'ArrowLeft':
          if (selectedIndex > -1 || (currentBlock && currentBlock.cbType === 'rating-scale')) {
            setLeftTrigger(prev => !prev);
          }
          break;

        case 'K':
          if (e.shiftKey) {
            e.preventDefault();
            e.target.blur();
            setShortcutsVisible(prev => !prev);
          }
          break;

        default:
          break;
      }
    },
    [
      shortcutsVisible,
      textInputInFocus,
      selectedIndex,
      currentBlock,
      onPrevious,
      nextQuestionByKey,
      setSelectedIndex,
      setRightTrigger,
      setLeftTrigger,
      setShortcutsVisible,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardShortcuts);
    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [handleKeyboardShortcuts]);

  useEffect(() => {
    setSelectedIndex(-1);
    if (currentBlock) {
      setCurrentCbId(currentBlock.cbId);
    }
  }, [currentBlock]);

  useEffect(() => {
    if (currentCbId) {
      void getPreviousResponses();
    }
  }, [currentCbId]);

  useEffect(() => {
    if (sessionEnded && isSessionEndedRef.current === false) {
      isSessionEndedRef.current = true;
    }
  }, [sessionEnded]);

  const handleOpenSessionJump = () => {
    setSelectedSection(Sections.OPEN_SESSION);
    setCurrentEvaluation(null);
    setCurrentBlock(null);
    setSelectedSubsection(null);
    setJumpToEvaluation('evaluation');
  };

  const onPreviousResponseSelected = (cbId, answers) => {
    if (dctSelectedResponses.some(response => response.cbId === cbId)) {
      setDctSelectedResponses(prev => prev.filter(response => response.cbId !== cbId));
    } else {
      setDctSelectedResponses(prev => [...prev, { cbId, answers }]);
    }
  };

  const onUseAllPreviousResponses = async (allNormal, allRisky) => {
    if (allNormal) {
      setPreviousResponseFlags(null);
      if (currentBlock.cbType === 'dct') {
        try {
          const filteredDCT = expandedConversation.dcts.find(dct => dct.dctId === currentBlock.dctId);

          let previousResponse = (filteredDCT?.contentBlocks || [])
            .map(cbId => {
              const cb = expandedConversation.contentBlocks.find(cb => cb.cbId === cbId);
              if (!cb) return null;

              const prevResp = getUserResponseInfo(cb.previousResponses);

              return {
                cbId,
                answers: [...prevResp],
              };
            })
            .filter(Boolean);
          const filteredPreviousResponse = previousResponse.filter(item => item.answers.length > 0);
          if (filteredPreviousResponse.length !== 0) {
            previousResponse = filteredPreviousResponse.filter(item =>
              item.answers.some(answer => answer.value !== null),
            );
          }
          setDctSelectedResponses(previousResponse);
          setNextInProgress(true);
          dispatch(
            conversationActionCreators.fetchEvaluationContext({
              appointmentId: appointment?.appointmentId,
              evaluationId: currentEvaluation?.id,
            }),
          );
        } catch (error) {
          console.log({ error });
        }
      }
    } else {
      setPreviousResponseFlags({ allNormal, allRisky });
    }
  };

  const onSkipEvaluation = () => {
    setJumpToEvaluation(currentEvaluation);
    const currentIndex = activeEvaluations.findIndex(e => e.id === currentEvaluation.id);
    if (currentIndex < activeEvaluations.length - 1) {
      list.setHead(null);
      setCurrentEvaluation(activeEvaluations[currentIndex + 1]);
    } else {
      setEvaluationsCompleted(true);
      setCurrentEvaluation(null);
    }
  };

  const onRejectEvaluation = evaluation => {
    setEvaluationToReject(evaluation);
  };

  const handleJumpToEvaluation = evaluation => {
    if (evaluation === 'evaluation') {
      setSelectedSection(Sections.EVALUATIONS);
      setJumpToEvaluation(null);
    } else {
      setSelectedSection(Sections.EVALUATIONS);
      setSelectedSubsection(evaluation.id);
      setCurrentEvaluation(evaluation);
      setJumpToEvaluation(null);
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (sessionEnded) {
      setExpanded(true);
    }
  }, [sessionEnded]);

  // Resize observer to detect compact mode
  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsCompactMode(width < 800);
      }
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const renderLeftAction = () => {
    if (selectedSection === Sections.CLOSE_SESSION)
      return (
        <Button sx={sx.restartBtn} endIcon={<Icons glyph="restart" />} onClick={onRestartSessionClick}>
          Restart session
        </Button>
      );
    else {
      let color = appointment?.sessionAnalysisAllowed ? colors.success500 : colors.destructive500;
      let text = appointment?.sessionAnalysisAllowed ? 'Analysis active' : 'Analysis deactivated';

      return (
        <Box display="flex" alignItems="center" gap="8px" marginLeft="16px">
          {appointment?.sessionAnalysisAllowed ? (
            <Icons glyph="analysis-active-lottie" color={colors.success500} />
          ) : (
            <img src={analysisInactiveIcon} alt="analysis inactive" width={18} height={18} />
          )}
          <Text level={textLevel.XS} weight={fontWeight.SEMI_BOLD} color={color}>
            {text}
          </Text>
        </Box>
      );
    }
  };

  const renderHelperText = () => {
    if (isCompactMode) return null;

    if (selectedSection === Sections.WELCOME && selectedSubsection === subsections.IMPORTANT_INFORMATION) {
      if (appointment?.sessionAnalysisAllowed)
        return (
          <Button
            disableRipple
            variant={btnType.TEXT}
            size={btnSize.XSMALL}
            onClick={() => setShortcutsVisible(true)}
          >
            <Icons glyph="keyboard" color={colors.neutral500} />
            <Text
              level={textLevel.XS}
              weight={fontWeight.SEMI_BOLD}
              color={colors.neutral500}
              marginLeft="6px"
            >
              Shortcuts (Shift + K)
            </Text>
          </Button>
        );
      else
        return (
          <Text
            level={textLevel.XS}
            weight={fontWeight.SEMI_BOLD}
            color={colors.neutral500}
            marginRight="20px"
          >
            Fill out responses manually
          </Text>
        );
    } else
      return (
        <Text level={textLevel.XS} weight={fontWeight.SEMI_BOLD} color={colors.neutral500} marginRight="20px">
          Or press Spacebar
        </Text>
      );
  };

  return (
    <Box sx={sx.root} ref={contentRef}>
      {/* Main section: Header + Navigator + Content + Footer */}
      <Box sx={sx.mainSection}>
        {/* Header */}
        <SessionHeader
          appointment={appointment}
          isEndedSession={sessionEnded}
          isSessionStarted={sessionStarted}
          onDurationUpdated={setDuration}
          onEndSession={showEndSessionWarning}
          startSignoff={startSignoff}
          goToSchedule={goToSchedule}
          showMarkAsNoShow={showMarkAsNoShow}
        />

        {/* Navigator and content wrapper */}
        <Box sx={sx.contentWrapper}>
          <SessionNavigator
            isFirstAppointment={appointmentDetails?.firstAppointment}
            sessionEnded={sessionEnded}
            selectedSection={selectedSection}
            setSelectedSection={setSelectedSection}
            selectedEvaluationId={selectedSubsection}
            setSelectedEvaluationId={setSelectedSubsection}
            currentEvaluation={currentEvaluation}
            setCurrentEvaluation={setCurrentEvaluation}
            currentBlock={currentBlock}
            setCurrentBlock={setCurrentBlock}
            expanded={isCompactMode ? false : expanded}
            toggleExpand={() => setExpanded(!expanded)}
            setExpanded={setExpanded}
            listHelper={list}
            isPrescriber={isPrescriber}
            exerciseHelper={exerciseList}
            treatmentPlan={treatmentPlan}
            onMemberActionClick={handleMemberActionClick}
            setEvaluationClicked={setEvaluationClicked}
            setScrollToTop={setScrollToTop}
            getPreviousResponsesForDCT={getPreviousResponsesForDCT}
            isCompactMode={isCompactMode}
          />
          <SectionDetail
            section={selectedSection}
            subSection={selectedSubsection}
            idFront={demographicDetails && demographicDetails.idCardFront}
            idBack={demographicDetails && demographicDetails.idCardBack}
            onEndSession={showEndSessionWarning}
            showKeyboardShortcuts={setShortcutsVisible}
            appointment={appointment}
            nextAppointment={nextAppointment}
            nextGeneralAppointment={nextGeneralAppointment}
            onRestartSessionClick={onRestartSessionClick}
            openEvaluationsDrawer={openEvaluationsDrawer}
            onAddEvaluation={onAddEvaluation}
            jumpToOpenSession={handleOpenSessionJump}
            scratchPadText={appointmentDetails?.scratchPad ?? ''}
            selectedIndex={selectedIndex}
            goToSchedule={goToSchedule}
            currentBlock={currentBlock}
            currentEvaluation={currentEvaluation}
            setTextInputInFocus={setTextInputInFocus}
            evaluationsCompleted={evaluationsCompleted}
            sessionEnded={sessionEnded}
            onNext={onNext}
            profileElements={profileElements}
            listHelper={list}
            exerciseHelper={exerciseList}
            onSkipEvaluation={onSkipEvaluation}
            onRejectEvaluation={onRejectEvaluation}
            dctPreviousResponses={dctPreviousResponses}
            dctSelectedResponses={dctSelectedResponses}
            onPreviousResponseSelected={onPreviousResponseSelected}
            leftTrigger={leftTrigger}
            rightTrigger={rightTrigger}
            onPrevious={onPrevious}
            onMemberActionClick={handleMemberActionClick}
            previousResponses={previousResponses}
            useAllPreviousResponses={onUseAllPreviousResponses}
            treatmentPlan={treatmentPlan}
            modalities={modalities}
            expandedEvaluation={expandedConversation}
            jumpToEvaluation={jumpToEvaluation}
            handleJumpToEvaluation={handleJumpToEvaluation}
            scrollToTop={scrollToTop}
            setScrollToTop={setScrollToTop}
            aiMode={aiMode}
            setAiMode={setAiMode}
            onScratchpadUpdate={handleScratchpadUpdate}
          />
        </Box>

        {/* Footer with action buttons */}
        <Box
          sx={{
            ...sx.actionsContainer,
            backgroundColor: appointment?.sessionAnalysisAllowed ? colors.white : colors.destructive50,
          }}
        >
          {/* Left side - Analysis status */}
          {renderLeftAction()}

          {/* Right side - Dynamic text + Continue button */}
          <Box display="flex" alignItems="center" gap="16px">
            {/* Dynamic helper text based on step and analysis status */}
            {!(selectedSection === Sections.OPEN_SESSION && !sessionEnded) &&
              !(selectedSection === Sections.CLOSE_SESSION) &&
              renderHelperText()}

            {/* Main action button */}
            {selectedSection === Sections.OPEN_SESSION && !sessionEnded ? (
              <Button
                sx={appointment?.sessionAnalysisAllowed ? sx.endBtn : sx.endBtnInverted}
                onClick={showEndSessionWarning}
              >
                End Session
              </Button>
            ) : selectedSection === Sections.CLOSE_SESSION ? (
              <Button sx={sx.leaveBtn} variant={btnType.OUTLINE} onClick={goToSchedule}>
                Leave session
              </Button>
            ) : (
              <Button sx={sx.nextBtn} onClick={onNext}>
                Continue
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      <EndSession
        open={endSessionModalVisible}
        onClose={() => setEndSessionModalVisible(false)}
        onEndSession={onEndSession}
      />
      {previousResponseFlags && (
        <ConfirmUsePreviousModal
          open={!!previousResponseFlags}
          onClose={() => setPreviousResponseFlags(null)}
          previousResponseFlags={previousResponseFlags}
          onConfirm={() => {
            void onUseAllPreviousResponses(true, false);
          }}
        />
      )}
      {isRequiredQuestionModalOpen && (
        <SessionModalPopup
          icon="question-outlined"
          open={isRequiredQuestionModalOpen}
          title="Required Question"
          content="This question is required. Are you sure you want to continue without answering it?"
          lbtnLabel="Cancel"
          rbtnLabel="Continue"
          onClose={() => {
            setIsRequiredQuestionModalOpen(false);
            isMoveNextAllowed.current = false;
          }}
          onCancel={() => {
            setIsRequiredQuestionModalOpen(false);
            isMoveNextAllowed.current = false;
          }}
          onSubmit={() => {
            isMoveNextAllowed.current = true;
            setIsRequiredQuestionModalOpen(false);
            void submitAnswer(true);
            setScrollToTop(true);
          }}
          isLoading={false}
        />
      )}
      {isHaveAllQuestionsBeenAskedModalOpen && (
        <SessionModalPopup
          icon="question-outlined"
          open={isHaveAllQuestionsBeenAskedModalOpen}
          title="Have all questions been asked?"
          content="Before proceeding to the next section, please confirm that all questions in this section have been asked."
          lbtnLabel="Cancel"
          rbtnLabel="Continue"
          onClose={() => setIsHaveAllQuestionsBeenAskedModalOpen(false)}
          onCancel={() => setIsHaveAllQuestionsBeenAskedModalOpen(false)}
          onSubmit={() => {
            setIsHaveAllQuestionsBeenAskedModalOpen(false);
            void submitAnswer(true);
            setScrollToTop(true);
          }}
          isLoading={false}
        />
      )}
      <KeyboardShortcutsModal onClose={() => setShortcutsVisible(false)} open={shortcutsVisible} />
      {openAddEvaluation && allEvaluations?.length > 0 && (
        <AddEvaluation
          isOpen={openAddEvaluation}
          onClose={() => setOpenAddEvaluation(false)}
          evaluations={allEvaluations}
          assignedEvaluations={evaluations}
          appointment={appointment}
          onAddEvaluation={onAddEvaluation}
          onRemoveEvaluation={handleEvaluationDeletion}
        />
      )}
      {evaluationToReject && (
        <RejectEvaluationModal
          open={!!evaluationToReject}
          onClose={() => {
            setEvaluationToReject(null);
          }}
          evaluation={evaluationToReject}
          updateEvaluationStatus={updateEvaluationStatus}
          setToastProperties={setToastProperties}
        />
      )}
    </Box>
  );
};

export { SessionContent };
